import { ChevronRight } from 'lucide-react';
import { ReactComponent as SvgSnowflake } from 'public/assets/icons/snowflakeFlat.svg';
import { ReactComponent as SvgMarker } from 'public/assets/icons/marker.svg';
import { cn } from '@/util/cn';
import { ImageComponent } from '@/components/common/ImageComponent';
import { IFeaturedResort } from '@/api/home/types';
import { IResortPathInfo, IDescriptionsResort } from '@/api/resort/types';
import { Button } from '@/components/ui/button';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { textOnly } from '@/util/sanitized';
import { Routes } from '@/routes';

// Featured-resort card used on the home page and 7 region pages.
// Layout: image-with-snowflake-decoration column + descriptive content
// column, swap order at xl+. The `isOnRegionSkiResorts` modifier expands
// into the sidebar gutter on region ski-resorts pages — relies on the
// `--sidebar-width` / `--sidebar-gap` CSS vars (Layout currently
// doesn't publish those; Tier 7 follow-up will restore the calc).
// NOTE (OWR-2402): restoring that calc widens the widget *under* the
// right rail, so it has to come back together with a rail-aware guard —
// otherwise the card body itself re-creates the overlap this fix removed.
//
// OWR-2402: the decoration is kept inside the card by *placing* it
// inside, not by cropping it. The section used to flip to
// `overflow-visible` at xl so the snowflake — 407px wide and offset to
// `right: -55%` — could bleed past the card edge. Every template that
// renders this widget puts it in <Main> beside a <Side> rail, so at xl+
// that bleed landed on top of the Related News module and the rail ad
// slot, which must never be overlapped.
//
// Clipping it instead was worse: it left a hard-edged wedge at the card
// boundary. So the fix is placement, not cropping. The xl geometry now
// matches what the region skireport template already did — a smaller
// 224px flake pushed *left* (`right: 90%`) into the card body, where it
// renders whole with the entire width of the card between it and the
// rail (measured: ~434px of clearance at 1440px).
//
// That makes the no-overlap guarantee structural rather than a crop: the
// flake is anchored to the *left* of its containing block, so no card
// width can carry it back toward the rail. `xl:overflow-visible` is
// therefore kept, so the artwork still bleeds softly past the card's top
// edge the way it does on skireport today — clipping it there would
// shave 22px off the top of the reference rendering.
//
// `isolate` bounds the negative-z decoration's stacking context to the
// card so it can never paint over a sibling column.

const sectionBaseClass = [
  'relative isolate overflow-hidden',
  'lg:mb-[4.75rem]',
  'xl:flex xl:flex-row-reverse xl:items-center xl:gap-[95px] xl:overflow-visible',
  '[&>div]:relative xl:[&>div]:flex-1',
].join(' ');

const sectionRegionSkiResortsClass = [
  'p-0',
  'lg:w-[calc(100%+var(--sidebar-width,0px)+var(--sidebar-gap,0px))]',
  'lg:mb-12',
  'max-xl:px-12 max-xl:pt-4',
  'max-sm:px-5 max-sm:pt-4',
].join(' ');

const sectionRegionExceptSkiResortsClass = [
  'p-0 pt-8',
  'max-xl:px-12',
  'max-sm:px-5',
  '[&>div>div:first-child>img]:!aspect-[1.338]',
  'xl:[&>div>div:first-child>img]:!min-h-[22.1875rem] xl:[&>div>div:first-child>img]:!min-w-[29.6875rem]',
  '[&>div>.snowflake>svg]:right-[-45%] [&>div>.snowflake>svg]:top-[75%]',
  'sm:[&>div>.snowflake>svg]:right-[-8%] sm:[&>div>.snowflake>svg]:top-[70%]',
  // These now match the shared xl default in `snowflakeClass`, but they
  // cannot be dropped as duplicates: the `[&>div>.snowflake>svg]` rules
  // above are more specific than the base `[&>svg]` ones, so without the
  // `!important` the sm offsets would win at xl too and push the flake
  // back out over the rail. Verified in a browser — OWR-2402.
  'xl:[&>div>.snowflake>svg]:!w-56 xl:[&>div>.snowflake>svg]:!h-56',
  'xl:[&>div>.snowflake>svg]:!right-[90%] xl:[&>div>.snowflake>svg]:!top-[-15%]',
].join(' ');

const imageWrapClass = [
  'relative z-[1]',
  '[&>img]:w-full [&>img]:object-cover [&>img]:rounded-[20px] [&>img]:aspect-[1.657]',
].join(' ');

const partnerBadgeClass = [
  'absolute top-5 right-0 z-[2]',
  'bg-secondary py-1.5 pl-6 pr-4 flex items-center',
  '[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,7%_50%)]',
  'max-md:py-1 max-md:pl-5 max-md:pr-3',
  '[&>p]:text-white [&>p]:font-medium [&>p]:text-sm [&>p]:leading-5',
  '[&>p]:tracking-[0.07em] [&>p]:m-0 [&>p]:uppercase [&>p]:whitespace-nowrap',
  'max-xl:[&>p]:text-xs max-xl:[&>p]:leading-4',
  'max-md:[&>p]:text-[10px] max-md:[&>p]:leading-[14px]',
].join(' ');

// Decoration only: `pointer-events-none` so the oversized SVG box can
// never swallow a click meant for the card CTA, and `-z-10` (scoped by
// the section's `isolate`) keeps it under the image, the copy and the
// CTA.
//
// The sub-xl offsets are unchanged — below xl the card has always
// cropped the flake at its right edge, and that is the shipped look.
// Only the xl placement moved, from `right: -55%` (outside the card, over
// the rail) to `right: 90%` (inside the card, left of the image), which
// is the placement the skireport template already used. See OWR-2402.
const snowflakeClass = [
  'snowflake pointer-events-none',
  '[&>svg]:absolute [&>svg]:w-[25.4375rem] [&>svg]:h-[24.375rem]',
  '[&>svg]:top-[67%] [&>svg]:right-[-55%] [&>svg]:-z-10',
  'sm:[&>svg]:w-[36%] sm:[&>svg]:top-[75%] sm:[&>svg]:right-[-15%]',
  'xl:[&>svg]:w-56 xl:[&>svg]:h-56',
  'xl:[&>svg]:right-[90%] xl:[&>svg]:top-[-15%]',
].join(' ');

const contentClass = [
  'my-7 max-w-[43.75rem]',
  '[&>h6]:font-semibold [&>h6]:text-xs [&>h6]:tracking-wider [&>h6]:uppercase',
  '[&>h6]:text-muted-foreground [&>h6]:mb-4',
  '[&>h3]:font-heading [&>h3]:font-semibold [&>h3]:text-2xl [&>h3]:capitalize',
  '[&>h3]:text-secondary [&>h3]:mb-3',
  'xl:[&>h3]:text-3xl xl:[&>h3]:mb-4',
  '[&>p]:text-base [&>p]:leading-relaxed [&>p]:text-foreground/85 [&>p]:my-6',
].join(' ');

const rowClass = [
  'flex flex-row items-center gap-[13px]',
  '[&>svg]:w-[15px] [&>svg]:h-[18px] [&>svg]:fill-muted-foreground',
  '[&>p]:m-0 [&>p]:text-sm [&>p]:font-medium [&>p]:text-muted-foreground',
  'xl:[&>p]:text-base',
].join(' ');

interface Props {
  resort: IFeaturedResort | null;
  regionResorts?: Array<IResortPathInfo>;
  resortDescriptionTabs?: Array<IDescriptionsResort>;
  isOnRegionSkiResorts?: boolean;
  isOnRegionExceptSkiResorts?: boolean;
  utmCampaign: string;
}

export const FeaturedResort = ({
  resort, regionResorts, resortDescriptionTabs, isOnRegionSkiResorts = false, isOnRegionExceptSkiResorts = false, utmCampaign,
}: Props) => {
  const { t } = useOTSTranslation('common');

  if (!resort) {
    return null;
  }

  const resortUuid = resort.uuid?.toString();
  const resortPathInfo = resortUuid && regionResorts
    ? regionResorts.find((item) => item.uuid === resortUuid)
    : undefined;

  if (!resortPathInfo) {
    return null;
  }

  const locationParts = [
    resort.region?.title,
    resort.region?.country,
  ].filter(Boolean) as Array<string>;

  const title = resort.title || t('homePage.featuredResort');
  const location = locationParts.length ? locationParts.join(', ').toUpperCase() : null;

  const resortProfileTab = resortDescriptionTabs?.filter((item) => item.tab === 'resort_profile');
  const rawDescription = resortProfileTab?.length ? resortProfileTab[0].description : null;

  // Plain-text teaser. Decode *before* truncating: the old code cut the raw
  // HTML at 250 characters, which could land inside an entity and print the
  // fragment (`…3450 Meter H&ou`). Going through textOnly also means the
  // result no longer has to be injected back as HTML to get the entities
  // decoded — it renders as an ordinary text child below.
  let description = rawDescription ? textOnly(rawDescription).trim() : null;
  if (description && description.length > 250) {
    description = `${description.substring(0, 250)}...`;
  }

  const baseHref = Routes.resortTripPlanningOverview(resortPathInfo);
  const utmParams = new URLSearchParams({
    utm_source: 'onthesnow',
    utm_medium: 'featured_resort',
    utm_campaign: utmCampaign,
    utm_content: resortPathInfo.uuid,
  });
  const linkHref = utmCampaign ? `${baseHref}?${utmParams.toString()}` : baseHref;

  return (
    <section
      id="featured-resort"
      className={cn(
        sectionBaseClass,
        isOnRegionSkiResorts && sectionRegionSkiResortsClass,
        isOnRegionExceptSkiResorts && sectionRegionExceptSkiResortsClass,
      )}
    >
      <div>
        <div className={imageWrapClass}>
          <ImageComponent alt={resort.title} src={resort.image ?? ''} />
          {resort.isPartner && (
            <div className={partnerBadgeClass}>
              <p>{t('common.premierPartner').toUpperCase()}</p>
            </div>
          )}
        </div>

        <div className={snowflakeClass} aria-hidden="true">
          <SvgSnowflake width="100%" height="100%" viewBox="0 0 176 193" />
        </div>
      </div>

      <div className={contentClass}>
        <h6>{t('homePage.featuredResort')}</h6>
        <h3>{title}</h3>
        {location && (
          <div className={rowClass}>
            <SvgMarker />
            <p>{location}</p>
          </div>
        )}
        {description ? (
          <p>{description}</p>
        ) : (
          <p>{t('home.featuredResort.noDescription')}</p>
        )}
        <Button asChild>
          <a href={linkHref}>
            {t('common.seeResortGuide')}
            <ChevronRight />
          </a>
        </Button>
      </div>
    </section>
  );
};
